import { apiClient } from "./client";
import { organizationsChanged } from "./orgEvents";
import { fetchAllPages } from "./paginate";
import type { OwnershipFields } from "./ownership";

export interface Distributor extends OwnershipFields {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  /** Registration token an MSP uses to onboard under this distributor. Read-only; rotate via rotateToken. */
  token?: string;
  contact_email?: string;
  msp_count?: number;
  /** When true, MSPs with no distributor FK (e.g. public MSP) roll up here */
  is_default?: boolean;
  /** White-label branding. Cascades to every MSP and company beneath, unless an MSP brands its own branch. */
  enable_branding?: boolean;
  logo?: string | null;
  logo_dark?: string | null;
  favicon?: string | null;
  primary_color?: string;
  accent_color?: string;
  brand_colors?: Record<string, string>;
  product_name?: string;
}

/** Recognized brand-color token keys (mirror of backend BRAND_COLOR_KEYS). */
export type BrandColorKey =
  | "primary" | "accent" | "secondary" | "ring"
  | "sidebar" | "sidebar_accent" | "sidebar_foreground"
  | "chart_1" | "chart_2" | "chart_3" | "chart_4" | "chart_5"
  | "success" | "warning" | "info";

/** Read-only branding payload from the branding endpoints. */
export interface DistributorBranding {
  name?: string;
  enable_branding: boolean;
  product_name?: string;
  logo?: string | null;
  logo_dark?: string | null;
  favicon?: string | null;
  /** token → #rrggbb (already merged with legacy primary/accent on the server). */
  colors?: Record<string, string>;
}

export interface BrandingUpdate {
  enable_branding?: boolean;
  product_name?: string;
  brand_colors?: Record<string, string>;
  /** The single-colour columns `brand_colors` replaced; empty clears one. */
  primary_color?: string;
  accent_color?: string;
}

/** Asset fields that accept a file upload. */
export type BrandingAsset = "logo" | "logo_dark" | "favicon";

/**
 * What the upload picker offers for a branding asset: the raster formats the
 * API accepts. Not SVG, which the API refuses because an SVG can carry script.
 * `.ico` by extension, since browsers disagree on the type they report for it.
 */
export const BRANDING_IMAGE_ACCEPT =
  "image/png,image/jpeg,image/gif,image/webp,.ico";

/** Payload for creating a distributor. Slug is required by the backend. */
export interface DistributorCreateInput {
  name: string;
  slug: string;
  is_active?: boolean;
  contact_email?: string;
}

/** Partial payload for updating a distributor. `owner` assigns the responsible user. */
export type DistributorUpdateInput = Partial<DistributorCreateInput> & {
  /** User UUID (TW-45637889). */
  owner?: string | null;
};

/**
 * Headers that opt a request out of the global scope narrowing.
 *
 * For navigators only: a list whose purpose is "what could I select" must not
 * be filtered by what is already selected. See the note in `api/client.ts`.
 *
 * Platform admins only. Every other role is refused with 403 "Scope 'all' is
 * not permitted for your role", so a navigator below that tier asks for
 * `BRANCH_SCOPE` instead.
 */
export const UNSCOPED = { "X-Context-Scope": "all" } as const;

/**
 * Headers that pin a request to the caller's own branch, whatever the scope
 * picker currently holds.
 *
 * `default` is the one scope the backend exempts from the role check, and it
 * resolves to the caller's home organization and everything under it, which is
 * the set a navigator wants. Sending no header at all is not the same thing:
 * the interceptor then fills one in from the persisted `contextStore`, so a
 * distributor admin who last picked a company in the credentials sidebar gets
 * zero distributors and zero MSPs back and a tree that looks complete.
 */
export const BRANCH_SCOPE = { "X-Context-Scope": "default" } as const;

/**
 * Which set a navigator is asking for: everything the platform holds, or
 * everything under the caller's own organization.
 */
export type ListScope = "all" | "branch";

export function scopeHeaders(
  scope?: ListScope,
): Record<string, string> | undefined {
  if (scope === "all") return { ...UNSCOPED };
  if (scope === "branch") return { ...BRANCH_SCOPE };
  return undefined;
}

export const distributorsApi = {
  /**
   * Every Distributor in scope, following the server's pages to the end.
   *
   * Takes no `page_size`. The old one was misleading twice over: the server
   * capped it at 100 whatever was asked, and the screens that passed nothing at
   * all rendered the first 25 as though that were the whole list.
   */
  list: async (opts?: { scope?: ListScope }): Promise<Distributor[]> => {
    return fetchAllPages<Distributor>(
      "/distributors/",
      scopeHeaders(opts?.scope),
    );
  },

  /**
   * One distributor. Pass `{ scope: "branch" }` when it is the caller's own:
   * with the scope picker below it the distributor is outside that scope, and
   * the plain request answers 404.
   */
  retrieve: async (id: string, opts?: { scope?: ListScope }): Promise<Distributor> => {
    const response = await apiClient.get(`/distributors/${id}/`, {
      headers: scopeHeaders(opts?.scope),
    });
    return response.data;
  },

  /** Issue a new registration token, invalidating the previous one. */
  rotateToken: async (
    id: string,
    opts?: { scope?: ListScope },
  ): Promise<{ token: string }> => {
    const response = await apiClient.post(`/distributors/${id}/rotate-token/`, undefined, {
      headers: scopeHeaders(opts?.scope),
    });
    return response.data;
  },

  create: async (data: DistributorCreateInput): Promise<Distributor> => {
    const response = await apiClient.post("/distributors/", data);
    organizationsChanged();
    return response.data;
  },

  update: async (
    id: string,
    data: DistributorUpdateInput,
  ): Promise<Distributor> => {
    const response = await apiClient.patch(`/distributors/${id}/`, data);
    organizationsChanged();
    return response.data;
  },

  destroy: async (id: string): Promise<void> => {
    await apiClient.delete(`/distributors/${id}/`);
    organizationsChanged();
  },

  /** Effective branding for the current user: the nearest branded distributor or MSP. */
  getBranding: async (): Promise<DistributorBranding> => {
    const response = await apiClient.get("/distributors/branding/");
    return response.data;
  },

  /** Update branding (colors map / product name / toggle). Distributor admins patch their own row. */
  updateBranding: async (id: string, data: BrandingUpdate): Promise<Distributor> => {
    const response = await apiClient.patch(`/distributors/${id}/`, data, {
      headers: { ...BRANCH_SCOPE },
    });
    return response.data;
  },

  /** Upload a branding asset (logo, dark logo, or favicon) via multipart. */
  uploadAsset: async (id: string, field: BrandingAsset, file: File): Promise<Distributor> => {
    const form = new FormData();
    form.append(field, file);
    const response = await apiClient.patch(`/distributors/${id}/`, form, {
      headers: { ...BRANCH_SCOPE, "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
};
